import type { MenuType } from '@/modules/Customer/interface/Menu';
import { FC, useMemo } from 'react';
import { ChevronRight, type IconsNames } from '@eqi-investimentos/icons/web';
import {
  Button,
  ButtonProps,
  Link,
  LinkProps,
  PopoverTrigger,
  Text,
} from '@eqi-investimentos/react';
import { Icon } from '@/components/elements/Icon';

export const MenuItem: FC<MenuItemProps> = ({
  iconName,
  isActive = false,
  name,
  target = '_self',
  url,
  onClick,
  isOverflow = false,
}) => {
  const Child = useMemo(
    () => (
      <>
        {iconName && <Icon iconName={iconName} width={24} height={24} />}
        <Text
          size={isOverflow ? 'xl' : 'm'}
          textAlign={isOverflow ? 'left' : 'center'}
          minW="6.75rem"
          maxW="max-content"
          whiteSpace="nowrap"
        >
          {name}
        </Text>
        {isOverflow && !url && (
          <ChevronRight boxSize="1.5rem" marginLeft="auto" />
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isActive, isOverflow]
  );

  return url ? (
    <Link href={url} target={target} {...styles(isActive, isOverflow)}>
      {Child}
    </Link>
  ) : (
    <PopoverTrigger>
      <Button
        variant="naked"
        onClick={onClick}
        {...styles(isActive, isOverflow)}
      >
        {Child}
      </Button>
    </PopoverTrigger>
  );
};

const styles = (
  isActive: boolean,
  isOverflow: boolean
): LinkProps & ButtonProps => {
  const hoverOrFocus = isOverflow
    ? {
        '& svg, & p': {
          color: 'action.active.brandLight',
        },
      }
    : {
        bgColor: 'background.brandLight',
        borderTopRadius: 'xs',
        borderBottomRadius: isActive ? '0' : 'xs',
      };

  return {
    display: 'flex',
    flexDir: isOverflow ? 'row' : 'column',
    alignItems: 'center',
    gap: 's',
    py: 'm',
    px: 'xs',
    w: isOverflow ? 'full' : 'fit-content',
    color: isActive || isOverflow ? 'content.brand' : 'content.secondary',
    borderBottomWidth: isOverflow ? '1px' : '3px',
    borderBottomColor: isOverflow
      ? 'transparency.light'
      : isActive
        ? 'content.brand'
        : 'transparent',
    _hover: hoverOrFocus,
    _focusVisible: hoverOrFocus,
  };
};

type MenuItemProps = Pick<MenuType, 'name' | 'target'> & {
  iconName: IconsNames | null;
  isActive?: boolean;
  onClick: () => void;
  isOverflow?: boolean;
  url?: string;
};
